import {
  useLayoutEffect,
  useState,
  type CSSProperties,
  type RefObject,
} from 'react'

export const useDropdownPosition = (
  triggerRef: RefObject<HTMLButtonElement | null>,
  open: boolean,
  position: 'auto' | 'top' | 'bottom',
  maxHeight: number,
  usePortal: boolean,
  dropdownMinWidth?: number
) => {
  const [layout, setLayout] = useState<{
    dropdownPosition: 'top' | 'bottom'
    portalStyle: CSSProperties
  }>({ dropdownPosition: 'bottom', portalStyle: {} })

  useLayoutEffect(() => {
    if (!open) return
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const gutter = 8
      const gap = 4
      const below = Math.max(0, window.innerHeight - rect.bottom - gap - gutter)
      const above = Math.max(0, rect.top - gap - gutter)
      const dropdownPosition =
        position === 'auto'
          ? below < maxHeight && above > below
            ? 'top'
            : 'bottom'
          : position
      const panelWidth = Math.min(
        Math.max(rect.width, dropdownMinWidth || 0),
        window.innerWidth - gutter * 2
      )
      setLayout({
        dropdownPosition,
        portalStyle: usePortal
          ? {
              position: 'fixed',
              left: Math.max(
                gutter,
                Math.min(rect.left, window.innerWidth - panelWidth - gutter)
              ),
              right: 'auto',
              width: panelWidth,
              minWidth: panelWidth,
              maxWidth: panelWidth,
              maxHeight: Math.min(
                maxHeight,
                dropdownPosition === 'top' ? above : below
              ),
              boxSizing: 'border-box',
              marginTop: 0,
              zIndex: 99999,
              top: dropdownPosition === 'bottom' ? rect.bottom + gap : 'auto',
              bottom:
                dropdownPosition === 'top'
                  ? window.innerHeight - rect.top + gap
                  : 'auto',
            }
          : {},
      })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [triggerRef, open, position, maxHeight, usePortal, dropdownMinWidth])

  return layout
}
